/**
 * Typed actions — the only path the UI uses to send client messages. Wraps the
 * raw socket so components never assemble protocol envelopes by hand.
 *
 * The seat is intentionally NOT a parameter here for `bid`/`playCard` — the
 * wire protocol has no `seat` field (the server fills it from the
 * authenticated connection; ARCH §9.1, anti-cheat by construction). A
 * misbehaving component cannot forge another player's move because the field
 * doesn't exist on the wire.
 *
 * v1.2 (RULES §4.2): the `pass` action is BACK (Phase 12). Sends a bare
 * `{ t: "pass" }` message; the server fills the seat from the
 * authenticated connection. The 10-s auction-ends timer still runs
 * server-side, but explicit pass lets a non-high-bidder opt out without
 * waiting for the clock. Per-seat permanent within a single auction
 * window — once you pass, the engine rejects further bid/pass moves
 * from your seat with `ALREADY_PASSED`.
 */
import type { ClientMessage, Suit } from "@hookum/protocol";
import type { HookumSocket } from "../net/socket.js";
import type { Store } from "../state/store.js";
import { saveName } from "../state/storage.js";

export interface Actions {
  joinRoom(code: string, name: string): void;
  /** Tear down the socket AND reset the store so the App routes back to the
   * RoomGate. Callers — Lobby's Leave button, GameOver's "Back to lobby" —
   * rely on this dual effect to avoid stranding the user on the same screen
   * (Phase 3 review Finding #2). */
  disconnect(): void;
  setConfig(players: 4 | 6, jokers: boolean, teamMode: "manual" | "random"): void;
  assignSeat(seatIndex: number, playerId: string): void;
  start(): void;
  bid(value: number, trump: Suit): void;
  /** v1.2 (RULES §4.2): explicit pass during the open auction sub-phase. */
  pass(): void;
  /** v1.4 Phase 20 (bidding skip): the current high-bidder toggles their
   * "ready to end the auction" flag. The 10-s timer is bypassed when this
   * is true AND every non-high-bidder has passed. Server rejects unless
   * the seat is the current high bidder and the open sub-phase is active. */
  toggleReady(): void;
  playCard(cardId: string): void;
  /** v1.2 Phase 14: ack the round-end / game-end Continue gate. The server
   * matches the seat by authenticated connection (no payload). All humans
   * must ack before the round advances (or the room returns to lobby). */
  continueRound(): void;
  /** v1.4.1 Phase 22.1 (rematch flow): host-only request to start a fresh
   * game directly from the GameOver screen. Server enforces host + the
   * `game_over` Continue gate; non-host or wrong-phase calls receive an
   * `error` message. `resetType` controls partner pairing for the next
   * game — `same_seats` keeps seating identical; `shuffle_partners` swaps
   * seats {1,2} (4p) / {1,2}+{3,4} (6p) so partner pairings change. */
  rematch(resetType: "same_seats" | "shuffle_partners"): void;
}

export function createActions(socket: HookumSocket, store: Store): Actions {
  const send = (m: ClientMessage): void => socket.send(m);
  return {
    joinRoom(code, name) {
      saveName(name);
      socket.connect(code, name);
    },
    disconnect() {
      socket.disconnect();
      store.reset();
    },
    setConfig(players, jokers, teamMode) {
      send({ t: "setConfig", players, jokers, teamMode });
    },
    assignSeat(seatIndex, playerId) {
      send({ t: "assignSeat", seatIndex, playerId });
    },
    start() {
      send({ t: "start" });
    },
    bid(value, trump) {
      send({ t: "bid", value, trump });
    },
    pass() {
      send({ t: "pass" });
    },
    toggleReady() {
      send({ t: "toggleReady" });
    },
    playCard(cardId) {
      send({ t: "playCard", cardId });
    },
    continueRound() {
      send({ t: "continueRound" });
    },
    rematch(resetType) {
      send({ t: "rematch", resetType });
    },
  };
}
